import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAsync, useSession, useToast } from '../lib/store';
import type { BrowseResponse, Listing, ListingKind, PurchaseResult } from '../lib/types';
import { Button, EmptyState, ErrorState, LoadingGrid, Panel, Tabs } from '../components/ui';
import { ListingCard } from '../components/ListingCard';
import { formatNumber } from '../lib/format';

type Sort = 'new' | 'cheap' | 'dear' | 'ending';

/**
 * The market floor.
 *
 * Prices here are whatever players decided they are — the server never suggests
 * one. What it *does* publish is what things have actually sold for, which is
 * the difference between a transparent market and one where new players get
 * quietly fleeced.
 */
export function BazaarPage() {
  const { me, setWallet, refresh } = useSession();
  const { push } = useToast();

  const [kind, setKind] = useState<ListingKind | 'ALL'>('ALL');
  const [sort, setSort] = useState<Sort>('new');
  const [shiny, setShiny] = useState(false);
  /**
   * Breeding filters — design PART 24. Held in one object rather than a state
   * hook each, so adding a facet is one line here and one control below rather
   * than four edits that can drift out of step.
   */
  const [breeding, setBreeding] = useState<Record<string, string>>({});
  const [showBreeding, setShowBreeding] = useState(false);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  // Any change to the query starts again at the first page — staying on page 7
  // of a different search is never what anyone meant.
  const breedingKey = JSON.stringify(breeding);
  useEffect(() => setPage(0), [kind, sort, shiny, debounced, breedingKey]);

  const state = useAsync<BrowseResponse>(
    () =>
      api.get('/bazaar', {
        kind: kind === 'ALL' ? undefined : kind,
        sort,
        shiny: shiny ? '1' : undefined,
        search: debounced || undefined,
        page,
        // Blank values are dropped rather than sent empty: an empty string is a
        // filter that matches nothing, which reads to a player as "the Bazaar
        // is broken" rather than "you left a box blank".
        ...Object.fromEntries(Object.entries(breeding).filter(([, value]) => value !== '')),
      }),
    [kind, sort, shiny, debounced, page, breedingKey],
  );

  const setFilter = (key: string, value: string) =>
    setBreeding((current) => ({ ...current, [key]: value }));
  const activeFilters = Object.values(breeding).filter((value) => value !== '').length;

  async function buy(listing: Listing) {
    setBusy(listing.id);
    try {
      const result = await api.post<PurchaseResult>(`/bazaar/listings/${listing.id}/buy`, {
        quantity: 1,
      });
      if (result.wallet) setWallet(result.wallet);
      push('reward', `Bought ${result.what} for ${formatNumber(result.total)} coins.`, '⛁');
      state.reload();
      void refresh();
    } catch (caught) {
      const message = caught instanceof ApiError ? caught.message : 'Could not buy that.';
      push('error', message);
      // "Someone else got there first" means the shelf has moved on.
      if (caught instanceof ApiError && (caught.code === 'GONE' || caught.code === 'EXPIRED')) {
        state.reload();
      }
    } finally {
      setBusy(null);
    }
  }

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;

  const data = state.data;

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">The Bazaar</h1>
          <div className="page-head__sub">
            Every stall here belongs to another trainer. They set the prices — the game does not.
          </div>
        </div>
        <Link className="btn btn--go" to="/bazaar/mine">
          My Bazaar
        </Link>
      </div>

      <div className="pxframe market-bar">
        <input
          className="input"
          value={search}
          placeholder="Search a species, item or egg family…"
          onChange={(event) => setSearch(event.target.value)}
        />
        <select className="select" value={sort} onChange={(event) => setSort(event.target.value as Sort)}>
          <option value="new">Newest</option>
          <option value="cheap">Cheapest first</option>
          <option value="dear">Priciest first</option>
          <option value="ending">Ending soonest</option>
          <option value="generation">Highest generation</option>
        </select>
        <Button size="sm" variant={shiny ? 'special' : 'default'} onClick={() => setShiny((on) => !on)}>
          ✦ Shiny only
        </Button>
        <Button
          size="sm"
          variant={activeFilters > 0 ? 'special' : 'default'}
          onClick={() => setShowBreeding((on) => !on)}
        >
          Breeding{activeFilters > 0 ? ` (${activeFilters})` : ''}
        </Button>
      </div>

      {/* Collapsed by default. A casual buyer wants a species and a price; the
          eleven facets a breeder wants would bury both. */}
      {showBreeding && (
        <div className="pxframe market-bar market-bar--wrap">
          <select className="select" value={breeding.gender ?? ''} onChange={(e) => setFilter('gender', e.target.value)}>
            <option value="">Any gender</option>
            <option value="female">Female</option>
            <option value="male">Male</option>
            <option value="genderless">Genderless</option>
          </select>
          <select className="select" value={breeding.sizeClass ?? ''} onChange={(e) => setFilter('sizeClass', e.target.value)}>
            <option value="">Any size</option>
            <option value="XXS">XXS (Miniature)</option>
            <option value="XS">XS</option>
            <option value="S">Small</option>
            <option value="M">Average</option>
            <option value="L">Large</option>
            <option value="XL">XL</option>
            <option value="XXL">XXL (Titan)</option>
          </select>
          <select className="select" value={breeding.marking ?? ''} onChange={(e) => setFilter('marking', e.target.value)}>
            <option value="">Any marking</option>
            <option value="star">Star</option>
            <option value="heart">Heart</option>
            <option value="crescent">Crescent</option>
            <option value="patch">Patched</option>
            <option value="striped">Striped</option>
            <option value="speckled">Speckled</option>
            <option value="freckled">Freckled</option>
          </select>
          <select className="select" value={breeding.heritage ?? ''} onChange={(e) => setFilter('heritage', e.target.value)}>
            <option value="">Any shiny heritage</option>
            <option value="1">Some shiny ancestry</option>
            <option value="3">A shiny parent</option>
            <option value="4">Strong shiny lineage</option>
            <option value="5">Exceptional shiny lineage</option>
          </select>
          <input
            className="input"
            type="number"
            min={0}
            placeholder="Min generation"
            value={breeding.minGeneration ?? ''}
            onChange={(e) => setFilter('minGeneration', e.target.value)}
          />
          <input
            className="input"
            placeholder="Bred by…"
            value={breeding.breeder ?? ''}
            onChange={(e) => setFilter('breeder', e.target.value)}
          />
          {activeFilters > 0 && (
            <Button size="sm" variant="ghost" onClick={() => setBreeding({})}>
              Clear
            </Button>
          )}
        </div>
      )}

      <Tabs
        value={kind}
        onChange={setKind}
        tabs={[
          { value: 'ALL', label: 'Everything' },
          { value: 'POKEMON', label: 'Pokémon' },
          { value: 'EGG', label: 'Eggs' },
          { value: 'ITEM', label: 'Items' },
        ]}
      />

      {state.loading && !data ? (
        <LoadingGrid count={8} height={220} />
      ) : (data?.listings.length ?? 0) === 0 ? (
        <EmptyState art="⛁" title="Nothing on the stalls">
          {debounced
            ? 'Nobody is selling that right now. Try a different search.'
            : 'The market is empty — be the first to put something up.'}
        </EmptyState>
      ) : (
        <Panel
          title="◈ On the stalls"
          hint={`${formatNumber(data!.total)} listings · ${data!.fee}% seller fee`}
        >
          <div className="stall-grid">
            {data!.listings.map((listing) => (
              <ListingCard
                key={listing.id}
                listing={listing}
                busy={busy === listing.id}
                onBuy={
                  listing.seller.id === me?.user.id ? undefined : (row) => void buy(row)
                }
              />
            ))}
          </div>

          {(page > 0 || data!.hasMore) && (
            <div className="row row--between" style={{ marginTop: 'var(--gap)' }}>
              <Button size="sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                ← Back
              </Button>
              <span className="tiny muted mono">
                page {page + 1} of {Math.max(1, Math.ceil(data!.total / data!.pageSize))}
              </span>
              <Button size="sm" disabled={!data!.hasMore} onClick={() => setPage((p) => p + 1)}>
                More →
              </Button>
            </div>
          )}
        </Panel>
      )}
    </div>
  );
}
